import cloneDeep from 'lodash.clonedeep';
import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import {
  EricssonNETargetInfoFIXTargetFormData,
  EricssonNETargetInfoFormData,
  EricssonNETargetInfoGPRSTargetFormData,
  EricssonNETargetInfoMonitoringFormData,
  EricssonNETargetInfoMSCTargetFormData,
  EricssonNETargetInfoTargetFormData,
  TargetModelAfCCCSettings,
  TargetModelAccessFunction,
  EricssonNETargetInfoMCTargetFormData,
  ERICSSON_NE_MC_KEY,
  ERICSSON_NE_TARGET_KEY
} from 'data/target/target.types';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import { KeyValuePair } from 'data/types';
import { TargetDeliveryTemplateAccessFunction, TargetDeliveryTemplateSection } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { getTemplateOptions } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import { DEPLOYMENT_TUNISIA } from 'xcipio/constants';
import { errorMessages, getMessage } from '../util';
import { getNextTemporaryId } from './ericssonNETargetInfoTemplate.factory';

const ERICSSON_NE_TAG = 'ERICSSON_NE';

export function isEricssonNEAccessFunctionSupported(
  accessFunction: AccessFunction,
  selectedTargetType: string,
): boolean {
  if (accessFunction.tag !== ERICSSON_NE_TAG) {
    return false;
  }

  if (accessFunction.additionalMOs?.[0] == null) {
    return false;
  }

  const neTypeOption = accessFunction.additionalMOs[0].options.find(({ key }) => key === 'netype');
  if (neTypeOption == null) {
    return false;
  }

  if (selectedTargetType === 'IMEI' && neTypeOption.value === 'HLR') {
    return false;
  }

  return (neTypeOption.value === 'GPRS'
    || neTypeOption.value === 'MSC'
    || neTypeOption.value === 'HLR'
    || neTypeOption.value === 'FIX'
    || neTypeOption.value === 'SIPSERVER'
    || neTypeOption.value === 'OV_SIPSERVER'
    || neTypeOption.value === 'OV_MOWLAN'
    || neTypeOption.value === 'OV_GPRS');
}

export function isEricssonNETargetInfoSupported(
  accessFunction?: { tag?: string, x3TargetAssociations?: string },
): boolean {
  if (accessFunction == null) {
    return false;
  }

  return accessFunction.tag === ERICSSON_NE_TAG && accessFunction.x3TargetAssociations === 'EIMSTARGET';
}

export function isEricssonNETargetInfoValid(
  accessFunctionSettings: TargetModelAfCCCSettings[],
  targetType: string,
  errors: string[],
  errorMsgPrefix: string | null,
): boolean {
  if (targetType === 'CASEID') {
    return true;
  }

  if (accessFunctionSettings.length === 0) {
    errors.push(getMessage(errorMessages.REQUIRED_TARGET, errorMsgPrefix, targetType));
    return false;
  }

  const isValid = accessFunctionSettings.every(({ isValid: isSettingValid }) => isSettingValid !== false);

  if (!isValid) {
    errors.push(getMessage(errorMessages.REQUIRED_TARGET, errorMsgPrefix, targetType));
  }

  return isValid;
}

export function isGenbandC20TargetInfoValid(
  afSettings: { [key: string]: string | number | boolean | string[] },
  targetInfoHash: { [key: string]: string | number | boolean | string[] },
  targetType: string,
  errors: string[],
  errorMsgPrefix: string | null,
): boolean {

  if (afSettings == null || targetInfoHash == null) {
    errors.push(getMessage(errorMessages.REQUIRED_TARGET, errorMsgPrefix, targetType));
    return false;
  }

  const isValid = !!afSettings.GRPID && !!targetInfoHash.LIID;

  if (!isValid) {
    errors.push(getMessage(errorMessages.REQUIRED_TARGET, errorMsgPrefix, targetType));
  }

  return isValid;
}

export function adaptToForm(
  formData: Partial<EricssonNETargetInfoFormData>,
  accessFunctionSettings: TargetModelAfCCCSettings[],
  interceptType: string,
  netype: string,
): Partial<EricssonNETargetInfoFormData> {

  const targetDeliveryTemplate = accessFunctionSettings?.[0]?.options.find((option) => option.key === 'targetDeliveryTemplate');
  const target = accessFunctionSettings.filter(({ options }) => hasOptionFrom(options, 'EIMSTARGET'))
  const gprsTarget = accessFunctionSettings.filter(({ options }) => hasOptionFrom(options, 'EIMSGPRSTRGT'));
  const mscTarget = accessFunctionSettings.filter(({ options }) => hasOptionFrom(options, 'EIMSMSCTRGT'));
  const monitoringTarget = accessFunctionSettings.filter(({ options }) => hasOptionFrom(options, 'EIMSMCNB'));
  const fixTarget = accessFunctionSettings.filter(({ options }) => hasOptionFrom(options, 'EIMSWRLNTRGT'));
  const mcTarget = accessFunctionSettings.filter(({ options }) => hasOptionFrom(options, 'EIMSMC'));

  if (formData && formData.general) {
    formData.general.targetDeliveryTemplate = targetDeliveryTemplate?.value ?? '';
  }
  target.forEach(({ options }) => {
    options = options.filter((option) => ERICSSON_NE_TARGET_KEY.includes(option.key))
    const map = getOptionsAsMap(options) as EricssonNETargetInfoTargetFormData;
    formData.EIMSTARGET = map;
  });

  if (netype === 'GPRS') {
    gprsTarget.forEach(({ options }) => {
      const map = getOptionsAsMap(options) as EricssonNETargetInfoGPRSTargetFormData;
      formData.EIMSGPRSTRGT = map;
    });
  }

  if (netype === 'MSC') {
    mscTarget.forEach(({ options }) => {
      const map = getOptionsAsMap(options) as EricssonNETargetInfoMSCTargetFormData;
      formData.EIMSMSCTRGT = map;
    });
  }

  if (netype === 'FIX') {
    fixTarget.forEach(({ options }) => {
      const map = getOptionsAsMap(options) as EricssonNETargetInfoFIXTargetFormData;
      formData.EIMSWRLNTRGT = map;
    });
  }

  if (netype === 'OV_SIPSERVER' || netype === 'OV_MOWLAN' || netype === 'OV_GPRS') {
    mcTarget.forEach(({ options }) => {
      options = options.filter((option) => ERICSSON_NE_MC_KEY.includes(option.key))
      const map = getOptionsAsMap(options) as EricssonNETargetInfoMCTargetFormData;
      formData.EIMSMC = map;
    });
  }

  if (interceptType !== 'CD' && netype !== 'HLR' && netype !== 'OV_SIPSERVER' && netype !== 'OV_MOWLAN' && netype !== 'OV_GPRS') {
    if (monitoringTarget.length > 0) {
      formData.EIMSMCNB = [];
    }

    monitoringTarget.forEach(({ options }) => {
      const map = getOptionsAsMap(options) as EricssonNETargetInfoMonitoringFormData;
      map.temporaryId = getNextTemporaryId();
      formData.EIMSMCNB?.push(map);
    });
  }

  return formData;
}

export function adaptToSubmit(
  targetAccessFunctionId: string,
  formData: Partial<EricssonNETargetInfoFormData>,
  isValid: boolean,
  type: string | undefined,
): TargetModelAfCCCSettings[] {
  const afSettings: TargetModelAfCCCSettings[] = [];

  const afids = [targetAccessFunctionId];
  const autowired = false;

  let options = getMapAsOptions(formData.EIMSTARGET ?? {});
  if (options.length > 0) {
    afSettings.push({
      afids,
      autowired,
      isValid,
      options,
    });
  }

  options = getMapAsOptions(formData.EIMSGPRSTRGT ?? {});
  if (options.length > 0) {
    afSettings.push({
      afids,
      autowired,
      isValid,
      options,
    });
  }

  options = getMapAsOptions(formData.EIMSMSCTRGT ?? {});
  if (options.length > 0) {
    afSettings.push({
      afids,
      autowired,
      isValid,
      options,
    });
  }

  options = getMapAsOptions(formData.EIMSWRLNTRGT ?? {});
  if (options.length > 0) {
    afSettings.push({
      afids,
      autowired,
      isValid,
      options,
    });
  }

  options = getMapAsOptions(formData.EIMSMC ?? {});
  // if interface type is Data Only then set cc mc id and cc suppneid as empty value
  if (type === 'CD') {
    options.forEach((option) => {
      if (option.key === 'CC_MCID' || option.key === 'CC_SUPPNEID') {
        option.value = '';
      }
    })
  }
  if (options.length > 0) {
    // this options will be merged to the first options in afSetting
    const setting = afSettings[0];
    if (setting) {
      setting.options = setting.options.concat(options)
    } else {
      afSettings.push({
        afids,
        autowired,
        isValid,
        options,
      });
    }
  }

  formData.EIMSMCNB?.forEach((entry) => {
    options = getMapAsOptions(entry);
    afSettings.push({
      afids,
      autowired,
      isValid,
      options,
    });
  });

  return afSettings;
}

export function adaptTabData(
  formData: Partial<EricssonNETargetInfoFormData>,
  tabData: EricssonNETargetInfoMonitoringFormData,
): EricssonNETargetInfoMonitoringFormData {
  if (formData.EIMSMCNB == null || formData.EIMSMCNB.length === 0) {
    tabData.MCNBID = '1';
    return tabData;
  }

  const usedIds = new Array(10).fill(false);
  formData.EIMSMCNB.forEach((entry) => { usedIds[+entry.MCNBID - 1] = true; });
  const availableIndex = usedIds.findIndex((value) => !value);
  tabData.MCNBID = `${availableIndex + 1}`;

  return tabData;
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate,
  isEditing: boolean,
): FormTemplate {
  const clonedTemplate = cloneDeep(template);

  if (!isEditing) {
    setAllFieldsAsReadOnly(clonedTemplate);
  }

  return clonedTemplate;
}

function setAllFieldsAsReadOnly(template: FormTemplate): void {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }

    Object.entries(templateEntry.fields).forEach(([_fieldKey, fieldEntry]) => {
      fieldEntry.readOnly = true;
    });
  });
}

function getOptionsAsMap(options: KeyValuePair[]) {
  return options.map(convertToBoolean).reduce((previousValue, currentValue) => ({
    ...previousValue,
    [currentValue.key]: currentValue.value,
  }), {});
}

export function getMapAsOptions(
  map: { [key: string]: string | boolean },
) {
  const options: KeyValuePair[] = [];

  Object.entries(map ?? {}).forEach(([key, value]) => {
    if (key === 'USEONELEMF' && typeof value === 'boolean') {
      value = value ? '1' : '0';
    }

    options.push({ key, value: value as string });
  });

  return options;
}

function convertToBoolean({ key, value }: KeyValuePair) {
  if (key === 'USEONELEMF' && typeof value === 'string') {
    value = (value === '1') as any;
  }

  return { key, value };
}

function hasOptionFrom(
  options: KeyValuePair[],
  optionFrom: 'EIMSTARGET' | 'EIMSGPRSTRGT' | 'EIMSMCNB' | 'EIMSMSCTRGT' | 'EIMSWRLNTRGT' | 'EIMSMC',
): boolean {
  switch (optionFrom) {
    case 'EIMSTARGET':
      return options.some(({ key }) => key === 'LEMFID');

    case 'EIMSGPRSTRGT':
      return options.some(({ key }) => key === 'GGSNMONITOR');

    case 'EIMSMCNB':
      return options.some(({ key }) => key === 'MCNBID');

    case 'EIMSMSCTRGT':
      return options.some(({ key }) => key === 'DT_F31');

    case 'EIMSWRLNTRGT':
      return options.some(({ key }) => key === 'LEGALBASIS');
    case 'EIMSMC':
      return options.some(({ key }) => key === 'IRI_MCID');

    default:
      return false;
  }
}

export function getTargetTemplateValues(
  sectionName: string,
  targetTemplate: TargetDeliveryTemplateAccessFunction,
): any {
  if (!targetTemplate) {
    return null;
  }

  const templateSection = (targetTemplate.templateSections as TargetDeliveryTemplateSection[])
    .filter((section) => section.name === sectionName);

  if (!templateSection || templateSection.length === 0) {
    return null;
  }

  if (templateSection.length > 1 || sectionName === 'EIMSMCNB') {
    return templateSection.map((t) => {
      return t.options.reduce((previous, current) => {
        let { value } = current;
        if (value === 'true' || value === 'false') {
          value = value === 'true' || false;
        }
        return ({ ...previous, [current.key]: value });
      },
        {});
    });
  }
  return templateSection[0].options.reduce((previous, current) => {
    let { value } = current;
    if (value === 'true' || value === 'false') {
      value = value === 'true' || false;
    }
    return ({ ...previous, [current.key]: value });
  }, {});
}

export function adaptTemplate(
  template: FormTemplate,
  deployment: string,
  targetDeliveryTemplates: TargetDeliveryTemplateAccessFunction[],
  targetAccessFunction: TargetModelAccessFunction
): FormTemplate {
  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.hide = 'false';
  const templateOptions = getTemplateOptions(targetDeliveryTemplates || [], targetAccessFunction.tag || '');
  (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.options = [{ value: '', label: 'Select a template' }, ...templateOptions];
  (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.initial = '';
  if (templateOptions.length > 0) {
    // (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.initial = templateOptions[0].value;
  }
  return clonedTemplate;
}

export function handleTargetDeliveryTemplateUpdate(data: Partial<EricssonNETargetInfoFormData>, targetDeliveryTemplates: TargetDeliveryTemplateAccessFunction[], selectedTemplateId: string) {
  // eslint-disable-next-line eqeqeq
  const clonedTargetTemplate = cloneDeep(targetDeliveryTemplates.find((targetTemplate) => targetTemplate.id == selectedTemplateId));
  if (clonedTargetTemplate) {
    const targetTemplateValues = ['EIMSTARGET', 'EIMSGPRSTRGT', 'EIMSMCNB'].reduce((acc, sectionKey: string) => {
      acc[sectionKey] = getTargetTemplateValues(sectionKey, clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);
      return acc;
    }, {} as { [id: string]: any });

    Object.keys(data).forEach((sectionKey) => {
      if (sectionKey === 'EIMSMCNB') {
        data.EIMSMCNB = data.EIMSMCNB?.map((eimsmcnb: any, index: number) => {
          if (targetTemplateValues[sectionKey][index]) {
            return { ...eimsmcnb, ...targetTemplateValues[sectionKey][index] };
          }
          return { ...eimsmcnb };
        })
      } else {
        data[sectionKey as keyof EricssonNETargetInfoFormData] = {
          ...data[sectionKey as keyof EricssonNETargetInfoFormData],
          ...targetTemplateValues[sectionKey]
        };
      }
    });
  }

  data = {
    ...data,
    general: {
      targetDeliveryTemplate: selectedTemplateId
    },
  };
  return data;
}

export function adaptDataBasedOnDeployment(data: Partial<EricssonNETargetInfoFormData>, deployment: string, template: FormTemplate, targetDeliveryTemplates: TargetDeliveryTemplateAccessFunction[]) {
  const selectedTemplateId = (template.general as FormTemplateSection).fields.targetDeliveryTemplate.initial ?? '';
  if (!selectedTemplateId) {
    return data;
  }
  // eslint-disable-next-line eqeqeq
  return handleTargetDeliveryTemplateUpdate(data, targetDeliveryTemplates, selectedTemplateId as string);
}
